import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Avatar,
  EmptyState,
  type IColumn,
  IconButton,
  type INameValueRecordProps,
  type IRow,
  NameValueRecord,
  Table,
  Tooltip,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats, getLabelAsNumberInternationalFormat } from 'shared/lib'
import { NoResultsFound } from 'shared/ui/NoResultsFound/NoResultsFound'
import { type ContactStatistic } from 'entities/ContactStatistic'

import { useEntitiesContext } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { useTriggerDetailContext } from 'widgets/TriggerDetail/context/triggerDetailContext'
import { ContactStatisticBulkActions } from 'widgets/ContactStatistic/ui/ContactStatisticBulkActions/ContactStatisticBulkActions'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import styles from './styles.module.scss'

const SendFromCell = ({ row }: { row: IRow<ContactStatistic> }) => {
  const { inboxesStore, numbersStore } = useEntitiesContext()
  const inbox = inboxesStore.getItem(row.team_id)
  const number = numbersStore.getItem(row.number_id)

  const props: INameValueRecordProps = {
    name: 'Contact owner',
    icon: 'inboxOpen',
  }

  if (inbox instanceof Inbox && number) {
    props.name = inbox.name
    props.value = number.formatted_number
  }

  if (inbox instanceof Inbox && inbox.isRCS) {
    props.name = inbox.name
    props.icon = 'rcs'
  }

  return <NameValueRecord {...props} />
}
const getColumns = (onOpenContactsDetails: IOpenContactsDetails) => {
  const nameColumn: IColumn<ContactStatistic> = {
    field: 'full_name',
    name: 'Name',
    renderRowCell: (row, _, config) => {
      if (!row) return null

      return (
        <div className={styles.contactColumn}>
          <div className={styles.contactColumnName}>
            <Avatar info={row.avatarInfo} size={24} />
            <Typography
              variant={'body-md-regular'}
              ellipsis
              tooltipProps={{ label: row.full_name }}
              ariaLabel={'displayInfo'}
            >
              {row.full_name || '—'}
            </Typography>
          </div>
          <Tooltip label='Conversation' placement='top'>
            <IconButton
              icon='chatRead'
              variant='icon'
              color='tertiary'
              transparentInactive
              className={config?.hidden}
              disabled={onOpenContactsDetails.disabled}
              onClick={(event) => {
                event.stopPropagation()
                onOpenContactsDetails.open({
                  contactId: row.id,
                })
              }}
              ariaLabel='ContactRow_sendMessage'
            />
          </Tooltip>
        </div>
      )
    },
  }

  const numberColumn: IColumn<ContactStatistic> = {
    field: 'number',
    name: 'Phone',
    isSortable: false,
    renderRowCell: (row) => row.number && getLabelAsNumberInternationalFormat(row.number),
  }

  const emailColumn: IColumn<ContactStatistic> = {
    field: 'email',
    name: 'Email',
    isSortable: false,
  }

  const targetTimeColumn: IColumn<ContactStatistic> = {
    field: 'message_sent_at',
    name: 'Send time',
    renderRowCell: (row) =>
      row.message_sent_at &&
      uiStore.dayjs(row.message_sent_at).format(DayjsFormats.fullWithAtDash2),
  }

  const sendFromColumn: IColumn<ContactStatistic> = {
    isSortable: false,
    field: 'send_from',
    name: 'Send from',
    isHideByDefault: true,
    renderRowCell: (row) => <SendFromCell row={row} />,
  }

  return [nameColumn, numberColumn, emailColumn, sendFromColumn, targetTimeColumn]
}

export const ContactStatisticsTable = observer(() => {
  const {
    contactStatisticsItems,
    isNoData,
    total,
    isLoading,
    onPaginationContactsTableChange,
    page,
    scrollToTopTrigger,
    limit,
    contactStatisticsTableStore,
    onOpenContactsDetails,
    search,
    onSuccessDelete,
    contactsTagsModalStore,
  } = useTriggerDetailContext()
  const columns = useMemo(() => getColumns(onOpenContactsDetails), [onOpenContactsDetails])

  if (isNoData) {
    return (
      <EmptyState text={'No contacts found'} noIcon boxProps={{ flexGrow: 1, height: undefined }} />
    )
  }

  if (!isLoading && !contactStatisticsItems.length) {
    return <NoResultsFound boxProps={{ flexGrow: 1, height: undefined }} />
  }

  return (
    <div className={styles.wrapTable}>
      <Table
        ariaLabel={'ContactStatisticsTable'}
        columns={columns}
        rows={contactStatisticsItems}
        rowsCount={total}
        isLoading={isLoading}
        onChangePagination={onPaginationContactsTableChange}
        initPage={page}
        limit={limit}
        startPaginationSize={10}
        sxTable={{ marginRight: 2, width: 'calc(100% - 16px)' }}
        scrollToTOpTrigger={scrollToTopTrigger}
        withCheckbox
        store={contactStatisticsTableStore}
        headerLeftContentProps={{
          actionsContent: (
            <ContactStatisticBulkActions
              search={search}
              selectedContactIds={contactStatisticsTableStore.selectedIds}
              onSuccessDelete={onSuccessDelete}
              withNewActions
              contactsTagsModalStore={contactsTagsModalStore}
            />
          ),
        }}
      />
    </div>
  )
})
